/**
 * Converts the grouped response of `POST /entities/metrics/query` with
 * `groupBy: {kind: 'label', labelKey}` into a PlatinumData object, so a
 * label breakdown (e.g. "opinion" / "sentiment") can render through the
 * existing pie path (`CarbonChart` → `SocialChart` → `PlatinumGraphs`)
 * instead of bespoke chart plumbing — the same reasoning as
 * `utils/themeAIResultsTransform.ts`'s `buildDistributionPlatinumData`.
 *
 * `EntityMetricsGroup` mirrors the Go server's response shape
 * (`goapi/pkg/server/autocoder_entities.go`'s `EntityMetricsGroup`), the same
 * way `useSkyEntityMetrics.ts` and `useSkyEntityLeaderboard.ts` each declare
 * their own local copy rather than sharing one from `gotypes.ts` — the entity
 * metrics types are not mirrored there.
 */
import type { PlatinumData } from '../../types/carbon/models/PlatinumData'
import { PVecType } from '../../types/carbon/models/PVecType'
import type { EntityMetricValue } from '../../components/social-media/metrics-config'

export interface EntityMetricsGroup {
  /** With `groupBy: {kind: 'label', labelKey}` this is the label value — '' means unset. */
  key: string
  /** Distinct entities in this group contributing ANY requested metric. */
  entityCount: number
  metrics: Record<string, EntityMetricValue>
}

/** What an empty (`''`) label-group key renders as. */
export const UNTAGGED_LABEL = 'Untagged'

const DEFAULT_COLUMN_LABEL = 'Share of output'

/**
 * Builds a single-column PlatinumData table from label groups, one row per
 * group: `freq` is the group's entity count, `colpc` its SHARE of entities
 * across all returned groups — a fraction (0–1), matching `getYesPercentage`
 * and every other reader of a pie/colPct cell in this pipeline
 * (`convertPlatinumToTable` keeps `colpc` verbatim, and the pie generator's
 * `tooltipPercentFormatter` multiplies by 100 at display time). It is
 * therefore the ENTITY count's share, not the requested metric's value —
 * the metric only decides which entities the query counted.
 */
export function labelGroupsToPie(
  groups: EntityMetricsGroup[],
  columnLabel: string = DEFAULT_COLUMN_LABEL,
): PlatinumData {
  const total = groups.reduce((sum, g) => sum + g.entityCount, 0)

  return {
    top: {
      vecs: [{ type: PVecType.Code, label: columnLabel, allowPC: true }],
    },
    side: {
      vecs: groups.map((g) => ({
        type: PVecType.Code,
        label: g.key === '' ? UNTAGGED_LABEL : g.key,
        allowPC: true,
      })),
    },
    cells: {
      rows: groups.map((g) => ({
        cell: [{
          freq: g.entityCount,
          colpc: total > 0 ? g.entityCount / total : 0,
        }],
      })),
    },
  }
}

export default labelGroupsToPie
